/**
 * AI Model JPEGs use a neutral dark studio backdrop. Flood-fill from image edges through
 * low-chroma dark pixels only, then clear alpha — keeps saturated garment colors and interior shadows.
 * Ported from v4-web `stores-progressive-style-generator.component.ts`.
 */
export const removeNeutralDarkBackdrop = (imageUrl: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = (): void => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          resolve(imageUrl);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve(imageUrl);
          return;
        }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, w, h);
        const d = imageData.data;
        const chromaMax = 22;
        const valueMax = 108;

        const isCandidate = (x: number, y: number): boolean => {
          const o = (y * w + x) * 4;
          const r = d[o];
          const g = d[o + 1];
          const b = d[o + 2];
          const maxChannel = Math.max(r, g, b);
          const minChannel = Math.min(r, g, b);
          return maxChannel - minChannel < chromaMax && maxChannel < valueMax;
        };

        const visited = new Uint8Array(w * h);
        const queue: number[] = [];

        const tryPush = (x: number, y: number): void => {
          if (x < 0 || y < 0 || x >= w || y >= h) {
            return;
          }
          const p = y * w + x;
          if (visited[p]) {
            return;
          }
          if (!isCandidate(x, y)) {
            return;
          }
          visited[p] = 1;
          queue.push(p);
        };

        for (let x = 0; x < w; x += 1) {
          tryPush(x, 0);
          tryPush(x, h - 1);
        }
        for (let y = 0; y < h; y += 1) {
          tryPush(0, y);
          tryPush(w - 1, y);
        }

        let head = 0;
        while (head < queue.length) {
          const p = queue[head];
          head += 1;
          const x = p % w;
          const y = Math.floor(p / w);
          tryPush(x + 1, y);
          tryPush(x - 1, y);
          tryPush(x, y + 1);
          tryPush(x, y - 1);
        }

        for (let p = 0; p < w * h; p += 1) {
          if (visited[p]) {
            const o = p * 4;
            d[o + 3] = 0;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(imageUrl);
      }
    };

    img.onerror = (): void => {
      reject(new Error('Image load failed'));
    };

    img.src = imageUrl;
  });
